import React, {useCallback, useMemo, useState} from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useNavigate, useParams} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {GET_JOB, GET_JOB_EVENTS} from '../graphql/queries';
import {RETRY_JOB, CANCEL_JOB} from '../graphql/mutations';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import {useAuth} from '../contexts/AuthContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import GenerationProgress, {getStageLabelForType} from '../components/GenerationProgress';
import {formatJobType} from './GenerationJobsPage';
import {colors} from '../theme/colors';

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

/**
 * Parse an inputSummary string and render it visually.
 *
 * Extracts token count and cost from a trailing "(Nk tokens ~$X.XX)" suffix
 * and renders them as styled chips. The main text is rendered as-is.
 *
 * For progress strings like "Summarizing 5/100... (1.2k tokens ~$0.01)",
 * also renders a progress bar.
 */
function JobProgressSummary({summary}: {summary: string}) {
  // Extract trailing "(N tokens ~$X.XX)" suffix
  const tokenSuffix = summary.match(/\s*\(([\d,.]+[kM]?\s*tokens(?:\s*~?\$[\d.]+)?)\)\s*$/);
  const mainText = tokenSuffix ? summary.slice(0, -tokenSuffix[0].length).trim() : summary;

  // Parse token and cost from suffix
  let tokenLabel: string | null = null;
  let costLabel: string | null = null;
  if (tokenSuffix) {
    const tMatch = tokenSuffix[1].match(/([\d,.]+[kM]?\s*tokens)/);
    const cMatch = tokenSuffix[1].match(/(~?\$[\d.]+)/);
    if (tMatch) tokenLabel = tMatch[1];
    if (cMatch) costLabel = cMatch[1];
  }

  // Try to parse progress: "Action X/Y..."
  const progressMatch = mainText.match(/^(.+?)\s+(\d[\d,]*)\s*\/\s*(\d[\d,]*)/);
  if (progressMatch) {
    const action = progressMatch[1];
    const current = progressMatch[2];
    const total = progressMatch[3];
    const currentNum = parseInt(current.replace(/,/g, ''), 10);
    const totalNum = parseInt(total.replace(/,/g, ''), 10);
    const pct = totalNum > 0 ? Math.round((currentNum / totalNum) * 100) : 0;

    return (
      <Box sx={{mb: 1.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
          <Typography sx={{fontSize: 13, fontWeight: 500, color: '#374151'}}>{action}</Typography>
          <Typography sx={{fontSize: 13, color: '#6B7280'}}>{current} / {total}</Typography>
        </Box>
        <Box sx={{width: '100%', height: 6, bgcolor: '#F3F4F6', borderRadius: 3, mb: 0.75, overflow: 'hidden'}}>
          <Box sx={{height: '100%', width: `${pct}%`, bgcolor: '#3B82F6', borderRadius: 3, transition: 'width 0.3s ease'}} />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {tokenLabel && <Chip label={tokenLabel} size="small" sx={{height: 20, fontSize: 11, fontWeight: 500, bgcolor: '#F3F4F6', color: '#6B7280', '& .MuiChip-label': {px: 0.75}}} />}
          {costLabel && <Chip label={costLabel} size="small" sx={{height: 20, fontSize: 11, fontWeight: 600, bgcolor: '#DBEAFE', color: '#1D4ED8', '& .MuiChip-label': {px: 0.75}}} />}
        </Box>
      </Box>
    );
  }

  // Default: render main text + optional token/cost chips
  return (
    <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5}}>
      <Typography sx={{fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap'}}>{mainText}</Typography>
      {tokenLabel && <Chip label={tokenLabel} size="small" sx={{height: 20, fontSize: 11, fontWeight: 500, bgcolor: '#F3F4F6', color: '#6B7280', '& .MuiChip-label': {px: 0.75}}} />}
      {costLabel && <Chip label={costLabel} size="small" sx={{height: 20, fontSize: 11, fontWeight: 600, bgcolor: '#DBEAFE', color: '#1D4ED8', '& .MuiChip-label': {px: 0.75}}} />}
    </Box>
  );
}

export default function JobDetailPage() {
  useDocumentTitle('Jobs', 'Detail');

  const {user} = useAuth();
  const navigate = useNavigate();
  const {jobId} = useParams<{jobId: string}>();

  const {data, loading} = useQuery(GET_JOB, {
    variables: {id: jobId},
    skip: !user || !jobId,
    pollInterval: 3000,
  });

  const [retryJob, {loading: retrying}] = useMutation(RETRY_JOB, {
    variables: {jobId},
    refetchQueries: [
      {query: GET_JOB, variables: {id: jobId}},
      {query: GET_JOB_EVENTS, variables: {jobId}},
    ],
  });

  const [cancelJob, {loading: canceling}] = useMutation(CANCEL_JOB, {
    variables: {jobId},
    refetchQueries: [
      {query: GET_JOB, variables: {id: jobId}},
      {query: GET_JOB_EVENTS, variables: {jobId}},
    ],
  });

  const DEFAULT_EVENT_LIMIT = 100;
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [showInputs, setShowInputs] = useState(false);

  const {data: eventsData} = useQuery(GET_JOB_EVENTS, {
    variables: {jobId, limit: showAllEvents ? undefined : DEFAULT_EVENT_LIMIT},
    skip: !user || !jobId,
    pollInterval: 3000,
  });

  const job = data?.job;
  const events = React.useMemo(
    () => eventsData?.jobEvents || [],
    [eventsData?.jobEvents]
  );
  const totalEventCount: number = eventsData?.jobEventCount ?? events.length;
  const hasMoreEvents = totalEventCount > events.length;

  // Parse extraction events into structured rows
  const extractionResults = useMemo(() => {
    if (job?.type !== 'SOURCE_TEXT_EXTRACTION' || job?.status !== 'SUCCEEDED') return null;
    const rows: Array<{title: string; status: 'completed' | 'failed'; detail?: string}> = [];
    for (const event of events) {
      const meta = event.metadata;
      if (meta?.sourceItemId && event.stage === 'UPLOADING_AND_PARSING_FILES') {
        const title = event.message?.match(/Extracted text from "(.+?)"/)?.[1]
          || event.message?.match(/Failed to extract "(.+?)"/)?.[1]
          || meta.sourceItemId;
        const failed = event.message?.toLowerCase().includes('fail') || !!meta.error;
        rows.push({title, status: failed ? 'failed' : 'completed', detail: meta.error});
      }
    }
    if (rows.length === 0) return null;
    const completed = rows.filter(r => r.status === 'completed').length;
    const failed = rows.filter(r => r.status === 'failed').length;
    return {rows, completed, failed};
  }, [job?.type, job?.status, events]);

  // Parse suggestion events into structured data
  const suggestionResults = useMemo(() => {
    if (job?.type !== 'GROUP_SUGGESTION' || job?.status !== 'SUCCEEDED') return null;
    const resultData = job.resultData as any;
    const stats = resultData?.stats;
    const clusters: Array<{name: string; score: number; passed: boolean; itemCount?: number}> = [];

    for (const event of events) {
      const meta = event.metadata;
      if (meta?.clusterName && meta?.compositeScore !== undefined) {
        clusters.push({
          name: meta.clusterName,
          score: meta.compositeScore,
          passed: meta.passed ?? meta.compositeScore >= 0.5,
          itemCount: meta.itemCount,
        });
      }
    }

    return {
      count: resultData?.count ?? 0,
      stats,
      clusters,
    };
  }, [job?.type, job?.status, job?.resultData, events]);

  const handleComplete = useCallback((resultRef: string) => {
    // Navigate to guide edit page when complete
    navigate(`/org/guides/${resultRef}/edit`);
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{py: 4, px: 3}}>
        <Typography sx={{fontSize: 20, fontWeight: 600}}>Job not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{maxWidth: 800, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/org/jobs')}
        sx={{mb: 2, textTransform: 'none', color: '#6B7280'}}
      >
        Back to jobs
      </Button>

      {/* Header */}
      <Box sx={{mb: 3}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}>
          <Typography sx={{fontSize: 20, fontWeight: 650, color: colors.textPrimary}}>
            Job Details
          </Typography>
          <Chip
            label={job.status}
            size="small"
            color={
              job.status === 'SUCCEEDED' ? 'success' :
              job.status === 'FAILED' ? 'error' :
              job.status === 'RUNNING' ? 'info' : 'default'
            }
            variant={job.status === 'RUNNING' ? 'filled' : 'outlined'}
            sx={{height: 22, fontSize: 12}}
          />
          {(job.status === 'QUEUED' || job.status === 'RUNNING') && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={canceling ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
              onClick={() => cancelJob()}
              disabled={canceling}
              sx={{textTransform: 'none', fontSize: 12, height: 26}}
            >
              {canceling ? 'Canceling...' : 'Cancel'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Metadata */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1.5}}>Details</Typography>
        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1}}>
          <Box>
            <Typography sx={{fontSize: 12, color: '#9CA3AF', fontWeight: 600}}>TYPE</Typography>
            <Typography sx={{fontSize: 14}}>{formatJobType(job.type)}</Typography>
          </Box>
          <Box>
            <Typography sx={{fontSize: 12, color: '#9CA3AF', fontWeight: 600}}>CREATED</Typography>
            <Typography sx={{fontSize: 14}}>{formatTimestamp(job.createdAt)}</Typography>
          </Box>
          {job.startedAt && (
            <Box>
              <Typography sx={{fontSize: 12, color: '#9CA3AF', fontWeight: 600}}>STARTED</Typography>
              <Typography sx={{fontSize: 14}}>{formatTimestamp(job.startedAt)}</Typography>
            </Box>
          )}
          {job.finishedAt && (
            <Box>
              <Typography sx={{fontSize: 12, color: '#9CA3AF', fontWeight: 600}}>FINISHED</Typography>
              <Typography sx={{fontSize: 14}}>{formatTimestamp(job.finishedAt)}</Typography>
            </Box>
          )}
        </Box>

        {job.status === 'SUCCEEDED' && job.resultRef && (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/org/guides/${job.resultRef}/edit`)}
            sx={{mt: 2, textTransform: 'none'}}
          >
            Open draft
          </Button>
        )}

        {job.status === 'SUCCEEDED' && (job.resultData as any)?.tokenUsage && (() => {
          const rd = job.resultData as any;
          const cost = rd.costEstimate;
          const actualCost = rd.actualCost;
          const formatCost = (v: number) => v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
          return (
            <Box sx={{mt: 2, display: 'flex', flexWrap: 'wrap', gap: 3}}>
              {[
                {label: 'Input tokens', value: rd.tokenUsage.input?.toLocaleString()},
                {label: 'Output tokens', value: rd.tokenUsage.output?.toLocaleString()},
                {label: 'Total tokens', value: rd.tokenUsage.total?.toLocaleString()},
                ...(actualCost != null ? [
                  {label: 'Cost', value: formatCost(actualCost)},
                ] : cost ? [
                  {label: 'Est. cost', value: `~${formatCost(cost.totalCost)}`},
                ] : []),
                ...(cost?.model ? [{label: 'Model', value: cost.model}] : []),
              ].map(s => (
                <Box key={s.label}>
                  <Typography sx={{fontSize: 11, color: '#9CA3AF', fontWeight: 600}}>{s.label.toUpperCase()}</Typography>
                  <Typography sx={{fontSize: 14, fontWeight: 500}}>{s.value}</Typography>
                </Box>
              ))}
            </Box>
          );
        })()}
      </Box>

      {/* Job Configuration / Inputs */}
      {job.inputSnapshot && Object.keys(job.inputSnapshot).length > 0 && (() => {
        const snap = job.inputSnapshot as Record<string, unknown>;
        // Filter out empty/null values and internal fields
        const entries = Object.entries(snap).filter(([k, v]) =>
          v != null && v !== '' && k !== 'prompt' && k !== 'orgSlug'
        );
        // Show key fields inline, rest in collapsible
        const keyFields: Array<{label: string; value: string}> = [];
        if (snap.modelName) keyFields.push({label: 'Model', value: String(snap.modelName)});
        if (snap.promptName) keyFields.push({label: 'Prompt', value: String(snap.promptName)});
        if (snap.scanTasks) keyFields.push({label: 'Tasks', value: (snap.scanTasks as string[]).join(', ')});
        if (snap.resetTasks) keyFields.push({label: 'Reset', value: (snap.resetTasks as string[]).join(', ')});
        if (snap.fileIds) keyFields.push({label: 'Items', value: `${(snap.fileIds as string[]).length} selected`});
        const hasMore = entries.length > keyFields.length;

        return keyFields.length > 0 ? (
          <Box sx={{bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5, mb: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: keyFields.length > 0 ? 1.5 : 0}}>
              <Typography sx={{fontSize: 14, fontWeight: 600}}>Configuration</Typography>
              {hasMore && (
                <Button size="small" onClick={() => setShowInputs(!showInputs)} endIcon={showInputs ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{fontSize: 12, textTransform: 'none'}}>
                  {showInputs ? 'Less' : 'More'}
                </Button>
              )}
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 3}}>
              {keyFields.map(f => (
                <Box key={f.label}>
                  <Typography sx={{fontSize: 11, color: '#9CA3AF', fontWeight: 600}}>{f.label.toUpperCase()}</Typography>
                  <Typography sx={{fontSize: 14, fontWeight: 500}}>{f.value}</Typography>
                </Box>
              ))}
            </Box>
            {hasMore && (
              <Collapse in={showInputs}>
                <Box sx={{mt: 2, p: 1.5, bgcolor: '#F9FAFB', borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto'}}>
                  {JSON.stringify(snap, null, 2)}
                </Box>
              </Collapse>
            )}
          </Box>
        ) : null;
      })()}

      {/* Extraction Results Panel */}
      {extractionResults && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 600, mb: 0.5}}>Extraction Results</Typography>
          <Typography sx={{fontSize: 13, color: '#6B7280', mb: 1.5}}>
            {extractionResults.completed} extracted{extractionResults.failed > 0 ? `, ${extractionResults.failed} failed` : ''}
          </Typography>
          {extractionResults.rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': {borderBottom: 'none'},
              }}
            >
              {row.status === 'completed'
                ? <CheckCircleOutlineIcon sx={{fontSize: 16, color: colors.green?.[600] || '#16A34A'}} />
                : <ErrorOutlineIcon sx={{fontSize: 16, color: colors.red[600]}} />
              }
              <Typography sx={{fontSize: 13, flex: 1}}>{row.title}</Typography>
              <Chip
                label={row.status === 'completed' ? 'Completed' : 'Failed'}
                size="small"
                color={row.status === 'completed' ? 'success' : 'error'}
                variant="outlined"
                sx={{height: 20, fontSize: 11}}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Suggestion Results Panel */}
      {suggestionResults && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
            mb: 3,
          }}
        >
          <Typography sx={{fontSize: 14, fontWeight: 600, mb: 0.5}}>Suggestion Results</Typography>
          <Typography sx={{fontSize: 13, color: '#6B7280', mb: 1.5}}>
            {suggestionResults.count} suggestion{suggestionResults.count !== 1 ? 's' : ''} generated
            {suggestionResults.stats?.ungroupedCount != null && ` from ${suggestionResults.stats.ungroupedCount} ungrouped documents`}
          </Typography>

          {suggestionResults.clusters.length > 0 && suggestionResults.clusters.map((cluster, i) => (
            <Box
              key={i}
              sx={{
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': {borderBottom: 'none'},
              }}
            >
              {cluster.passed
                ? <CheckCircleOutlineIcon sx={{fontSize: 16, color: colors.green?.[600] || '#16A34A'}} />
                : <ErrorOutlineIcon sx={{fontSize: 16, color: '#9CA3AF'}} />
              }
              <Typography sx={{fontSize: 13, flex: 1}}>
                "{cluster.name}" — score {cluster.score.toFixed(2)}
                {cluster.itemCount != null && ` (${cluster.itemCount} docs)`}
              </Typography>
              {!cluster.passed && (
                <Chip label="Below threshold" size="small" variant="outlined" sx={{height: 20, fontSize: 11, color: '#9CA3AF', borderColor: '#D1D5DB'}} />
              )}
            </Box>
          ))}

          {suggestionResults.stats?.reason && (
            <Box sx={{mt: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.75}}>
              <InfoOutlinedIcon sx={{fontSize: 15, color: '#9CA3AF', mt: 0.2}} />
              <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>{suggestionResults.stats.reason}</Typography>
            </Box>
          )}

          {suggestionResults.stats && (
            <Box sx={{mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2}}>
              {[
                {label: 'Total items', value: suggestionResults.stats.totalItems},
                {label: 'Ungrouped', value: suggestionResults.stats.ungroupedCount},
                {label: 'Clusters formed', value: suggestionResults.stats.clustersFormed},
                {label: 'Passed', value: suggestionResults.stats.clustersPassed},
                {label: 'Rejected', value: suggestionResults.stats.clustersRejected},
              ].filter(s => s.value != null).map(s => (
                <Box key={s.label}>
                  <Typography sx={{fontSize: 11, color: '#9CA3AF', fontWeight: 600}}>{s.label.toUpperCase()}</Typography>
                  <Typography sx={{fontSize: 14, fontWeight: 500}}>{s.value}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Progress */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography sx={{fontSize: 14, fontWeight: 600, mb: 1}}>Progress</Typography>
        {job.inputSummary && <JobProgressSummary summary={job.inputSummary} />}
        {jobId && <GenerationProgress jobId={jobId} onComplete={handleComplete} />}
      </Box>

      {/* Events timeline */}
      {events.length > 0 && (
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            p: 2.5,
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5}}>
            <Typography sx={{fontSize: 14, fontWeight: 600}}>
              Events {totalEventCount > 0 ? `(${hasMoreEvents ? `showing ${events.length} of ${totalEventCount}` : totalEventCount})` : ''}
            </Typography>
            {hasMoreEvents && !showAllEvents && (
              <Button size="small" onClick={() => setShowAllEvents(true)} sx={{fontSize: 12, textTransform: 'none'}}>
                Show all {totalEventCount}
              </Button>
            )}
            {showAllEvents && totalEventCount > DEFAULT_EVENT_LIMIT && (
              <Button size="small" onClick={() => setShowAllEvents(false)} sx={{fontSize: 12, textTransform: 'none'}}>
                Show latest {DEFAULT_EVENT_LIMIT}
              </Button>
            )}
          </Box>
          {events.map((event: any, i: number) => (
            <Box
              key={i}
              sx={{
                py: 1,
                borderBottom: '1px solid #F3F4F6',
                '&:last-child': {borderBottom: 'none'},
                display: 'flex',
                gap: 2,
              }}
            >
              <Typography sx={{fontSize: 12, color: '#9CA3AF', minWidth: 80, flexShrink: 0}}>
                {new Date(event.timestamp).toLocaleTimeString()}
              </Typography>
              <Box>
                <Typography sx={{fontSize: 13, fontWeight: 500}}>
                  {event.message}
                </Typography>
                <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>
                  {event.stage ? getStageLabelForType(event.stage, job.type) : ''}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {job.errorMessage && (
        <Alert
          severity="error"
          sx={{mt: 3}}
          action={
            (job.status === 'FAILED' || job.status === 'CANCELED') && (
              <Button
                color="inherit"
                size="small"
                startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                onClick={() => retryJob()}
                disabled={retrying}
              >
                {retrying ? 'Retrying...' : 'Retry'}
              </Button>
            )
          }
        >
          {job.errorMessage}
        </Alert>
      )}
    </Box>
  );
}
